import React from 'react';
import { Group, Button, Text, Badge, Switch } from '@mantine/core';
import { IconRobot, IconSettings, IconArrowRight } from '@tabler/icons-react';

/**
 * ModeSwitcher - Toggle between Autopilot (AI-driven) and Co-pilot (expert-assisted) modes
 * Shows current mode and allows seamless switching
 */
export default function ModeSwitcher({ 
  currentMode = 'copilot', // 'autopilot' | 'copilot'
  onModeChange,
  disabled = false,
  showLabels = true,
  showAsToggle = false,
  showAsSwitch = false
}) {
  const isAutopilot = currentMode === 'autopilot';
  const isCopilot = currentMode === 'copilot';

  const handleSwitchToAutopilot = () => {
    onModeChange?.('autopilot');
  };

  const handleSwitchToCopilot = () => {
    onModeChange?.('copilot');
  };

  const handleToggle = () => {
    onModeChange?.(isAutopilot ? 'copilot' : 'autopilot');
  };

  if (showAsSwitch) {
    // Match autonomous/supervised switch design
    return (
      <Group gap="md" align="center" wrap="nowrap" style={{ 
        padding: '8px 12px', 
        borderRadius: '6px', 
        backgroundColor: 'var(--mantine-color-gray-0)',
        width: '100%',
        justifyContent: 'space-between'
      }}>
        <Text size="xs" c={isCopilot ? "blue" : "dimmed"} fw={isCopilot ? 600 : 400}>
          Co-pilot
        </Text>
        <Switch
          checked={isAutopilot}
          onChange={handleToggle}
          disabled={disabled}
          color="green"
          size="sm"
          styles={{
            track: { backgroundColor: isAutopilot ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-4)' }
          }}
        />
        <Text size="xs" c={isAutopilot ? "green" : "dimmed"} fw={isAutopilot ? 600 : 400}>
          Autopilot
        </Text>
      </Group>
    );
  }

  if (showAsToggle) {
    // Clean toggle style - no arrows, cleaner layout
    return (
      <Group gap="xs" align="center">
        {/* Autopilot Mode Button */}
        <Button
          variant={isAutopilot ? "filled" : "light"}
          color={isAutopilot ? "blue" : "gray"}
          size="sm"
          leftSection={<IconRobot size={16} />}
          onClick={handleSwitchToAutopilot}
          disabled={disabled}
        >
          Autopilot
          {isAutopilot && <Badge ml="xs" variant="light" color="blue" size="xs">Active</Badge>}
        </Button>
        
        {/* Co-pilot Mode Button */}
        <Button
          variant={isCopilot ? "filled" : "light"} 
          color={isCopilot ? "dark" : "gray"}
          size="sm"
          leftSection={<IconSettings size={16} />}
          onClick={handleSwitchToCopilot}
          disabled={disabled}
        >
          Co-pilot
          {isCopilot && <Badge ml="xs" variant="light" color="dark" size="xs">Active</Badge>}
        </Button>
      </Group>
    );
  }

  return (
    <Group gap="xs" align="center">
      {showLabels && (
        <Text size="sm" c="dimmed">Mode:</Text>
      )}
      
      {/* Autopilot Mode Button */}
      <Button
        variant={isAutopilot ? "filled" : "light"}
        color={isAutopilot ? "blue" : "gray"}
        size="sm"
        leftSection={<IconRobot size={16} />}
        onClick={handleSwitchToAutopilot}
        disabled={disabled || isAutopilot}
      >
        Autopilot
        {isAutopilot && <Badge ml="xs" variant="light" color="blue" size="xs">Active</Badge>}
      </Button>

      {!disabled && (
        <IconArrowRight size={16} color="#adb5bd" />
      )}
      
      {/* Co-pilot Mode Button */}
      <Button
        variant={isCopilot ? "filled" : "light"} 
        color={isCopilot ? "dark" : "gray"}
        size="sm"
        leftSection={<IconSettings size={16} />}
        onClick={handleSwitchToCopilot}
        disabled={disabled || isCopilot}
      >
        Co-pilot
        {isCopilot && <Badge ml="xs" variant="light" color="dark" size="xs">Active</Badge>}
      </Button>
    </Group>
  );
}